// /marketing — Marketing report (FB + IG výkonnost, web, SM Organic leady)
// 30.6.2026 feedback #25/#26: spolupráce se SM specialistkou ukončena — report už nehodnotí
// specialistku, ale výkon obsahu samotného (hodnocení jako SM specialista + tipy).

import React, { useRef, useState, useEffect } from "react";
import { TrendingUp, TrendingDown, ArrowRight, Activity, Users, Eye, EyeOff, MousePointerClick, Search, FileText, AlertTriangle, CheckCircle2, Clock, ExternalLink, Instagram, Facebook, Heart, MessageCircle, Calendar, RefreshCw, Globe, Sparkles, DollarSign, Target, BarChart3, ThumbsUp, Upload, CheckCircle, XCircle, Loader2, Megaphone } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka } from "../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, SkupinaPrehledu,
} from "../components/PrehledDlazdice.jsx";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, AreaChart, Area } from "recharts";
import { useApiData, apiFetch } from "../lib/api.js";
import InfoTip from "../components/InfoTip.jsx";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { formatDate } from "../lib/format.js";

const fmtInt = (n) => new Intl.NumberFormat("cs-CZ").format(Math.round(Number(n) || 0));
const fmtKc = (n) => `${fmtInt(n)} Kč`;
function fmtDateCz(iso) {
  if (!iso) return "—";
  return formatDate(iso) || iso;
}
function fmtRelDate(iso) {
  if (!iso) return "";
  return formatDate(iso);
}
function pctChange(before, during) {
  const b = Number(before) || 0;
  const d = Number(during) || 0;
  return b > 0 ? ((d - b) / b) * 100 : 0;
}

// === Hero verdikt ===
// 30.6.2026 feedback #26: spolupráce se SM specialistkou ukončena — verdikt už nehodnotí
// "pokračovat/ukončit spolupráci", ale výkon obsahu jako SM specialista + tipy co zlepšit.
function VerdictHero({ verdict, period }) {
  if (!verdict) return null;
  const colorMap = {
    red: { bg: "from-rose-500 to-red-600", iconBg: "bg-white/20", title: "Hodnocení: obsah nefunguje" },
    amber: { bg: "from-amber-500 to-orange-500", iconBg: "bg-white/20", title: "Hodnocení: smíšený výkon" },
    green: { bg: "from-emerald-500 to-teal-600", iconBg: "bg-white/20", title: "Hodnocení: obsah funguje" },
  };
  const c = colorMap[verdict.color] || colorMap.amber;
  const Icon = verdict.color === "red" ? AlertTriangle : verdict.color === "green" ? CheckCircle2 : Clock;
  // Zobrazit až 6 flagů per kategorie (ať se vejdou FB + IG metriky)
  const negFlags = (verdict.flags || []).filter((f) => f.type === "negative").slice(0, 6);
  const posFlags = (verdict.flags || []).filter((f) => f.type === "positive").slice(0, 6);

  return (
    <div className={`bg-gradient-to-br ${c.bg} rounded-2xl shadow-xl overflow-hidden`}>
      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-4 mb-5">
          <div className={`w-16 h-16 rounded-2xl ${c.iconBg} backdrop-blur-sm flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-white/80 font-bold mb-1">Verdikt</div>
            <div className="text-2xl sm:text-3xl font-black text-white leading-tight demo-sensitive">{c.title}</div>
            <div className="text-sm text-white/95 mt-2 leading-relaxed demo-sensitive">{verdict.recommendation}</div>
          </div>
        </div>

        {(negFlags.length > 0 || posFlags.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 demo-sensitive">
            {posFlags.length > 0 && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-[11px] uppercase tracking-wider text-white/90 font-bold mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" /> Co se zlepšilo
                </div>
                <ul className="space-y-1.5">
                  {posFlags.map((f, i) => <li key={i} className="text-sm text-white/95 leading-snug">• {f.text}</li>)}
                </ul>
              </div>
            )}
            {negFlags.length > 0 && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-[11px] uppercase tracking-wider text-white/90 font-bold mb-2 flex items-center gap-1.5">
                  <TrendingDown className="w-3.5 h-3.5" /> Co se zhoršilo
                </div>
                <ul className="space-y-1.5">
                  {negFlags.map((f, i) => <li key={i} className="text-sm text-white/95 leading-snug">• {f.text}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {(verdict.tips || []).length > 0 && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 mt-3 demo-sensitive">
            <div className="text-[11px] uppercase tracking-wider text-white/90 font-bold mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Tipy co zlepšit
            </div>
            <ul className="space-y-1.5">
              {verdict.tips.map((t, i) => <li key={i} className="text-sm text-white/95 leading-snug">• {t}</li>)}
            </ul>
          </div>
        )}

        <div className="text-xs text-white/80 mt-5 flex flex-wrap gap-x-4 gap-y-1 demo-sensitive">
          <span>Hodnocené období od <b className="text-white">{fmtDateCz(period?.collab_start)}</b></span>
          <span>{period?.during?.months} měs.</span>
        </div>
      </div>
    </div>
  );
}

// === ROI Panel ===
// === CSV parser pro Business Manager export (FB i IG) ===
//
// Soubory jsou UTF-16 LE s BOM, comma-separated, "sep=," header.
// Druhý řádek = title metriky (např. "Dosah", "Diváci", "Kliknutí na odkaz na Instagramu").
// Z titlu detekujeme PLATFORMU (fb / ig) i METRIKU (reach, engagement, ...).
const METRIC_HEADERS = {
  reach: ["divaci", "diváci", "dosah", "lifetime reach", "reach"],
  engagement: ["interakce", "interakce s obsahem", "engagement"],
  link_clicks: ["kliknuti na odkaz", "kliknutí na odkaz", "link clicks"],
  page_visits: ["navstevy", "návštěvy", "page visits", "navstev", "navstevy instagramoveho profilu"],
  new_followers: ["sledovani", "sledování", "novi fans", "noví fans", "new followers", "new fans"],
  impressions: ["zobrazeni", "zobrazení", "impressions", "zobrazení obsahu"],
};

// Detekce platformy z titulu metriky.
// "Dosah" → IG (FB tomu říká "Diváci")
// "instagramového", "na Instagramu" → IG
// "na Facebooku" → FB
// Default: pokud title neobsahuje žádný marker → fb (zachová backward compat)
function detectPlatform(headerTitle, fileName) {
  const t = String(headerTitle || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const f = String(fileName || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const all = t + " " + f;
  if (all.includes("instagram") || all.includes("instagramov") || t === "dosah" || f.includes("dosah")) return "ig";
  if (all.includes("facebook") || all.includes("facebookov")) return "fb";
  // "Diváci" je FB-specifický pojem, ale bez explicit FB marker
  if (t === "divaci" || t === "diváci") return "fb";
  // Default fallback
  return "fb";
}

function detectMetricKey(headerText) {
  const norm = String(headerText || "").toLowerCase().trim().normalize("NFD").replace(/[̀-ͯ]/g, "");
  for (const [key, candidates] of Object.entries(METRIC_HEADERS)) {
    for (const c of candidates) {
      const cNorm = c.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      if (norm === cNorm || norm.includes(cNorm)) return key;
    }
  }
  return null;
}

function parseDateToISO(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  // Czech "DD. MM. YYYY" nebo "DD.MM.YYYY"
  const m = s.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // US "MM/DD/YYYY"
  const u = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (u) return `${u[3]}-${u[1].padStart(2, "0")}-${u[2].padStart(2, "0")}`;
  return null;
}

async function parseCsvFile(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Detekce UTF-16 LE BOM (FB Business Manager export)
  let text;
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
    text = new TextDecoder("utf-16le").decode(buf.slice(2));
  } else if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
    text = new TextDecoder("utf-16be").decode(buf.slice(2));
  } else if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    text = new TextDecoder("utf-8").decode(buf.slice(3));
  } else {
    text = new TextDecoder("utf-8").decode(buf);
  }
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], metricKey: null, fileName: file.name };

  // Business Manager CSV má strukturu:
  //   řádek 0: "sep=,"  (separator hint)
  //   řádek 1: "Title metriky" (např. "Dosah", "Diváci", "Kliknutí na odkaz na Instagramu")
  //   řádek 2: "Datum","Primary"
  //   řádek 3+: data
  // Pokud první řádek začíná "sep=", tak metric title je řádek 1 a header je řádek 2.
  let titleLine = null;
  let headerLineIdx = 0;
  if (/^[﻿]?sep\s*=/i.test(lines[0])) {
    titleLine = (lines[1] || "").replace(/^"|"$/g, "").trim();
    headerLineIdx = 2;
  }
  const sep = (lines[headerLineIdx] || "").includes("\t") ? "\t" : ",";
  const headers = (lines[headerLineIdx] || "").split(sep).map((s) => s.replace(/^"|"$/g, "").trim());

  // Detekce metriky: priorita = title z řádku 1 → název souboru → header columns
  let metricKey = titleLine ? detectMetricKey(titleLine) : null;
  if (!metricKey) metricKey = detectMetricKey(file.name);
  // Detekce platformy ze stejných zdrojů
  const platform = detectPlatform(titleLine, file.name);

  // Najít date col + metric col
  const dateIdx = headers.findIndex((h) => /datum|date/i.test(h));
  let metricIdx = -1;
  if (metricKey) {
    // V CSV s "Primary" sloupcem je metric value pravděpodobně tam
    metricIdx = headers.findIndex((h) => /primary|hodnota|value/i.test(h));
    if (metricIdx < 0) {
      // Fallback: první nečíselný header který není date
      metricIdx = headers.findIndex((h, i) => i !== dateIdx);
    }
  } else {
    // Bez title — zkusíme detekovat z header textu
    for (let i = 0; i < headers.length; i++) {
      if (i === dateIdx) continue;
      const k = detectMetricKey(headers[i]);
      if (k) { metricKey = k; metricIdx = i; break; }
    }
  }
  if (dateIdx < 0 || !metricKey || metricIdx < 0) {
    return { rows: [], metricKey: null, platform, fileName: file.name, error: `Nelze rozpoznat sloupce v ${file.name} (title="${titleLine || "?"}", platforma=${platform})` };
  }
  const rows = [];
  // Data lines začínají PO header line (po "Datum","Primary")
  for (let i = headerLineIdx + 1; i < lines.length; i++) {
    const cols = lines[i].split(sep).map((s) => s.replace(/^"|"$/g, "").trim());
    const date = parseDateToISO(cols[dateIdx]);
    if (!date) continue;
    const valRaw = (cols[metricIdx] || "").replace(/\s/g, "").replace(",", ".");
    const val = Number(valRaw);
    if (Number.isNaN(val)) continue;
    rows.push({ date, [metricKey]: Math.round(val) });
  }
  return { rows, metricKey, platform, fileName: file.name };
}

// === Upload box vpravo v banneru ===
// Všech 6 očekávaných metrik per platforma — pro checklist zobrazení v review modalu
const REQUIRED_METRICS = [
  { key: "reach", labelFb: "Diváci", labelIg: "Dosah" },
  { key: "impressions", labelFb: "Zobrazení", labelIg: "Zobrazení" },
  { key: "engagement", labelFb: "Interakce s obsahem", labelIg: "Interakce s obsahem" },
  { key: "link_clicks", labelFb: "Kliknutí na odkaz", labelIg: "Kliknutí na odkaz" },
  { key: "page_visits", labelFb: "Návštěvy", labelIg: "Návštěvy profilu" },
  { key: "new_followers", labelFb: "Sledování", labelIg: "Sledování" },
];

function UploadBox({ onUploaded }) {
  const inputRef = useRef(null);
  // status: "idle" | "parsing" | "review" | "uploading" | "success" | "error"
  // V "review" stavu má state.parsed array { fileName, metricKey, platform, rows, error }
  const [state, setState] = useState({ status: "idle", message: "", parsed: [] });

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setState({ status: "parsing", message: `Zpracovávám ${files.length} ${files.length === 1 ? "soubor" : files.length < 5 ? "soubory" : "souborů"}…`, parsed: [] });
    try {
      const parsed = [];
      for (const file of files) {
        const { rows, metricKey, platform, error } = await parseCsvFile(file);
        parsed.push({ fileName: file.name, metricKey, platform, rows, error });
      }
      // Skok rovnou do review stavu — user uvidí co se rozpoznalo + může opravit FB/IG per file
      setState({ status: "review", message: "", parsed });
    } catch (e) {
      setState({ status: "error", message: e.message || "Parsing selhal.", parsed: [] });
      setTimeout(() => setState({ status: "idle", message: "", parsed: [] }), 6000);
    }
  };

  // Toggle FB ↔ IG pro konkrétní soubor v review stavu
  const togglePlatform = (idx) => {
    setState((s) => ({
      ...s,
      parsed: s.parsed.map((p, i) => i === idx ? { ...p, platform: p.platform === "fb" ? "ig" : "fb" } : p),
    }));
  };

  const cancelReview = () => setState({ status: "idle", message: "", parsed: [] });

  const confirmUpload = async () => {
    const parsed = state.parsed.filter((p) => p.metricKey && p.platform && !p.error);
    if (parsed.length === 0) {
      setState({ status: "error", message: "Žádný platný soubor k nahrání.", parsed: [] });
      setTimeout(() => setState({ status: "idle", message: "", parsed: [] }), 5000);
      return;
    }
    setState((s) => ({ ...s, status: "uploading", message: "Nahrávám…" }));
    try {
      const perPlatform = { fb: new Map(), ig: new Map() };
      parsed.forEach(({ rows, platform }) => {
        const map = perPlatform[platform];
        rows.forEach((r) => {
          const cur = map.get(r.date) || { date: r.date };
          map.set(r.date, { ...cur, ...r });
        });
      });
      const fbRows = [...perPlatform.fb.values()];
      const igRows = [...perPlatform.ig.values()];

      const results = [];
      if (fbRows.length > 0) {
        const r = await apiFetch("/api/upload-meta-history", {
          method: "POST",
          body: JSON.stringify({ platform: "fb", rows: fbRows }),
        });
        const j = await r.json();
        if (!r.ok || j.error) throw new Error(`FB upload: ${j.error || `HTTP ${r.status}`}`);
        results.push({ platform: "FB", ...j });
      }
      if (igRows.length > 0) {
        const r = await apiFetch("/api/upload-meta-history", {
          method: "POST",
          body: JSON.stringify({ platform: "ig", rows: igRows }),
        });
        const j = await r.json();
        if (!r.ok || j.error) throw new Error(`IG upload: ${j.error || `HTTP ${r.status}`}`);
        results.push({ platform: "IG", ...j });
      }
      const successMsg = results.map((r) => `${r.platform}: +${r.new_days} nových, ${r.updated_days} update`).join(", ");
      setState({ status: "success", message: `Hotovo. ${successMsg}`, parsed: [] });
      setTimeout(() => {
        onUploaded?.();
        setTimeout(() => setState({ status: "idle", message: "", parsed: [] }), 1500);
      }, 800);
    } catch (e) {
      setState({ status: "error", message: e.message || "Upload selhal.", parsed: [] });
      setTimeout(() => setState({ status: "idle", message: "", parsed: [] }), 6000);
    }
  };

  const busy = state.status === "parsing" || state.status === "uploading";
  return (
    <div className="flex flex-col items-end gap-1.5">
      <input
        ref={inputRef} type="file" multiple accept=".csv,text/csv,application/vnd.ms-excel"
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={`inline-flex items-center gap-2 font-bold px-4 py-2.5 rounded-xl text-sm shadow-sm transition-all whitespace-nowrap ${
          state.status === "success" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
          state.status === "error" ? "bg-rose-600 hover:bg-rose-700 text-white" :
          busy ? "bg-sky-500 text-white cursor-wait" :
          "bg-sky-600 hover:bg-sky-700 text-white"
        } disabled:opacity-90`}
      >
        {state.status === "parsing" || state.status === "uploading" ? <Loader2 className="w-4 h-4 animate-spin" /> :
         state.status === "success" ? <CheckCircle className="w-4 h-4" /> :
         state.status === "error" ? <XCircle className="w-4 h-4" /> :
         <Upload className="w-4 h-4" />}
        {state.status === "parsing" ? "Zpracovávám…" :
         state.status === "uploading" ? "Nahrávám…" :
         state.status === "success" ? "Hotovo" :
         state.status === "error" ? "Chyba" :
         "Nahrát CSV"}
      </button>
      {state.message && (
        <div className={`text-xs max-w-[260px] text-right leading-snug ${
          state.status === "error" ? "text-rose-700" :
          state.status === "success" ? "text-emerald-700" :
          "text-slate-600"
        }`}>{state.message}</div>
      )}
      {state.status === "idle" && (
        <div className="text-[11px] text-slate-500 leading-snug text-right max-w-[240px]">
          Vyber CSV z Business Manageru pro <b>Facebook</b> i <b>Instagram</b>. Platforma se rozpozná automaticky.
        </div>
      )}

      {/* === REVIEW MODAL — full-screen overlay s checklistem co se nahrává === */}
      {state.status === "review" && (
        <div className="fixed inset-0 bg-brand-dark/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={cancelReview}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-lg font-black text-slate-900">Zkontroluj rozpoznání před nahráním</div>
                <div className="text-xs text-slate-500 mt-0.5">{state.parsed.length} {state.parsed.length === 1 ? "soubor" : state.parsed.length < 5 ? "soubory" : "souborů"}. Klikni na FB/IG pokud byla detekce špatná.</div>
              </div>
              <button onClick={cancelReview} className="text-slate-400 hover:text-slate-700 p-1"><XCircle className="w-5 h-5" /></button>
            </div>

            {/* Per-file řádky s toggle FB/IG */}
            <div className="p-5 space-y-2 border-b border-slate-200">
              {state.parsed.map((p, i) => {
                const ok = !!p.metricKey && !p.error;
                return (
                  <div key={i} className={`flex items-center gap-3 p-2.5 rounded-xl border ${ok ? "bg-slate-50 border-slate-200" : "bg-rose-50 border-rose-200"}`}>
                    <FileText className={`w-4 h-4 flex-shrink-0 ${ok ? "text-slate-400" : "text-rose-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-slate-600 truncate">{p.fileName}</div>
                      {ok ? (
                        <div className="text-sm text-slate-900 font-semibold">
                          {REQUIRED_METRICS.find((m) => m.key === p.metricKey)?.[p.platform === "ig" ? "labelIg" : "labelFb"] || p.metricKey}
                          <span className="ml-2 text-xs text-slate-500">({p.rows.length} dnů)</span>
                        </div>
                      ) : (
                        <div className="text-sm text-rose-700">{p.error || "Nelze rozpoznat metriku."}</div>
                      )}
                    </div>
                    {ok && (
                      <div className="flex flex-shrink-0 rounded-lg overflow-hidden border border-slate-300">
                        <button
                          onClick={() => p.platform !== "fb" && togglePlatform(i)}
                          className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 transition-colors ${p.platform === "fb" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        ><Facebook className="w-3 h-3" />FB</button>
                        <button
                          onClick={() => p.platform !== "ig" && togglePlatform(i)}
                          className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 transition-colors ${p.platform === "ig" ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        ><Instagram className="w-3 h-3" />IG</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Checklist co je pokryto per platforma */}
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-slate-200 bg-slate-50">
              {["fb", "ig"].map((plat) => {
                const detected = new Set(state.parsed.filter((p) => p.platform === plat && p.metricKey).map((p) => p.metricKey));
                const total = REQUIRED_METRICS.length;
                const filled = REQUIRED_METRICS.filter((m) => detected.has(m.key)).length;
                const PlatIcon = plat === "fb" ? Facebook : Instagram;
                const platLabel = plat === "fb" ? "Facebook" : "Instagram";
                const platColor = plat === "fb" ? "blue" : "pink";
                return (
                  <div key={plat} className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <PlatIcon className={`w-4 h-4 text-${platColor}-600`} />
                        <div className="text-sm font-bold text-slate-900">{platLabel}</div>
                      </div>
                      <div className={`text-xs font-bold ${filled === total ? "text-emerald-700" : filled === 0 ? "text-slate-400" : "text-amber-700"}`}>
                        {filled} / {total}
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {REQUIRED_METRICS.map((m) => {
                        const has = detected.has(m.key);
                        return (
                          <li key={m.key} className="flex items-center gap-2 text-xs">
                            {has ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />}
                            <span className={has ? "text-slate-800 font-semibold" : "text-slate-400"}>
                              {plat === "ig" ? m.labelIg : m.labelFb}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="p-5 flex items-center justify-between gap-3">
              <button onClick={cancelReview} className="text-sm text-slate-600 hover:text-slate-900 font-semibold px-4 py-2 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0">
                Zrušit
              </button>
              <button
                onClick={confirmUpload}
                className="btn btn-primary"
              >
                <Upload className="w-4 h-4" />
                Nahrát {state.parsed.filter((p) => p.metricKey && !p.error).length} {state.parsed.filter((p) => p.metricKey && !p.error).length === 1 ? "soubor" : "souborů"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoiPanel({ roi, period, smLeadsDuring }) {
  const cost = roi?.total_cost_during_kc || 0;
  const monthly = roi?.sm_specialist_monthly_cost_kc || 0;
  const months = period?.during?.months || 0;
  // Reálné platby specialistce z cashflow (po ukončení spolupráce už nerostou).
  const paid = roi?.sm_specialist_paid_total_kc;
  const smActive = roi?.sm_specialist_active !== false;
  const lastPay = roi?.sm_specialist_last_payment || null;
  const payCount = roi?.sm_specialist_payments_count || 0;
  const fmtDate = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${+d}.${+m}.${y}`; };
  const platbySlovo = payCount === 1 ? "platba" : (payCount >= 2 && payCount <= 4 ? "platby" : "plateb");
  const leadsCount = smLeadsDuring?.total || 0;
  const dealsCount = smLeadsDuring?.deal_count || 0;
  const lossesCount = smLeadsDuring?.loss_count || 0;
  const pendingCount = smLeadsDuring?.pending_count || 0;
  // Tržba z dealů (s DPH) a bez DPH (skutečný příjem firmy = bez DPH).
  const revenueGross = roi?.sm_organic_revenue_during_kc || 0;
  const revenueNet = roi?.sm_organic_revenue_during_no_vat_kc || 0;
  const netResult = (roi?.sm_organic_net_during_kc != null) ? roi.sm_organic_net_during_kc : (revenueNet - cost);
  const costPerLead = leadsCount > 0 ? Math.round(cost / leadsCount) : 0;
  const costPerDeal = dealsCount > 0 ? Math.round(cost / dealsCount) : 0;

  let summary, summaryColor;
  if (dealsCount > 0 && revenueGross > 0) {
    summary = `${dealsCount} uzavřený deal přinesl ${fmtKc(revenueGross)} v tržbách (CAC ${fmtKc(costPerDeal)})`;
    summaryColor = "text-emerald-700";
  } else if (dealsCount > 0) {
    summary = `${dealsCount} uzavřený deal, náklad ${fmtKc(costPerDeal)} za získaného klienta`;
    summaryColor = "text-emerald-700";
  } else if (leadsCount > 0) {
    summary = `${leadsCount} ${leadsCount === 1 ? "lead" : "leadů"} přivezeno, žádný se zatím neuzavřel`;
    summaryColor = "text-amber-700";
  } else {
    summary = "Specialistka zatím nepřinesla žádný měřitelný lead přes SM Organic kanál";
    summaryColor = "text-rose-700";
  }

  // Barva NET tile podle ziskovosti (bez DPH vs náklad)
  const netPositive = netResult > 0;
  const netNeutral = netResult === 0;

  return (
    <div className="bg-white border-2 border-brand-200 rounded-2xl p-5 sm:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center">
          <DollarSign className="w-5 h-5" />
        </div>
        <div>
          <div className="text-base font-black text-slate-900">Návratnost investice</div>
          <div className="text-xs text-slate-500">Kolik to stojí, co to přineslo a jaký je čistý výsledek</div>
        </div>
      </div>

      {/* 4 dlaždice: Náklad měsíční | Celkem | Tržby z dealů | NET */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Měsíční náklad</div>
            <InfoTip text={smActive ? "Fixní měsíční fee, které platíme SM specialistce." : "Spolupráci se specialistkou už neplatíme, měsíční náklad je 0 Kč."} />
          </div>
          <div className="text-2xl font-black tabular-nums text-slate-900">{smActive ? fmtKc(monthly) : "0 Kč"}</div>
          <div className="text-xs text-slate-500 mt-1">{smActive ? "fixní fee specialistce" : `spolupráce ukončena${lastPay ? `, poslední ${fmtDate(lastPay)}` : ""}`}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Celkem doposud</div>
            <InfoTip text={paid != null ? "Součet reálných plateb specialistce z cashflow (účet 2905944013 / kategorie Marketing manager)." : `Měsíční fee × ${months.toFixed(1)} měsíců spolupráce.`} />
          </div>
          <div className="text-2xl font-black tabular-nums text-slate-900">{fmtKc(cost)}</div>
          <div className="text-xs text-slate-500 mt-1">{paid != null ? `${payCount} ${platbySlovo}, reálně z cashflow` : `${months.toFixed(1)} měs. spolupráce`}</div>
        </div>
        <div className={`${revenueGross > 0 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"} border rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className={`text-[11px] uppercase tracking-wider font-bold ${revenueGross > 0 ? "text-emerald-700" : "text-slate-500"}`}>Tržby z dealů</div>
            <InfoTip text="Součet všech faktur (s DPH) klientů, které specialistka přivedla přes SM Organic kanál a uzavřeli se. Match jména lead → FAPI faktura (přesný + fuzzy 2-token fallback). Trialové faktury se nepočítají." />
          </div>
          <div className={`text-2xl font-black tabular-nums ${revenueGross > 0 ? "text-emerald-900" : "text-slate-700"}`}>{fmtKc(revenueGross)}</div>
          <div className="text-xs text-slate-600 mt-1">{revenueNet > 0 ? `${fmtKc(revenueNet)} bez DPH` : "žádné faktury"}</div>
        </div>
        <div className={`${netPositive ? "bg-emerald-50 border-emerald-300" : netNeutral ? "bg-slate-50 border-slate-200" : "bg-rose-50 border-rose-200"} border-2 rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className={`text-[11px] uppercase tracking-wider font-bold ${netPositive ? "text-emerald-700" : "text-rose-700"}`}>Čistý výsledek</div>
            <InfoTip text="Čistý výsledek = tržby (bez DPH) minus celkový náklad. Plus znamená, že se specialistka už zaplatila. Mínus znamená, že firma stále investuje." />
          </div>
          <div className={`text-2xl font-black tabular-nums ${netPositive ? "text-emerald-900" : netNeutral ? "text-slate-900" : "text-rose-900"}`}>
            {netResult >= 0 ? "+" : ""}{fmtKc(netResult)}
          </div>
          <div className="text-xs text-slate-600 mt-1">{netPositive ? "už se zaplatila" : netNeutral ? "vyrovnaný stav" : "stále ztrátová"}</div>
        </div>
      </div>

      <div className={`text-sm font-semibold demo-sensitive ${summaryColor}`}>{summary}</div>

      {leadsCount > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="bg-slate-50 rounded-xl p-2.5">
            <div className="text-slate-500 mb-0.5">Náklad za 1 lead</div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{fmtKc(costPerLead)}</div>
          </div>
          {dealsCount > 0 && (
            <div className="bg-emerald-50 rounded-xl p-2.5">
              <div className="text-emerald-700 mb-0.5">Náklad za 1 klienta (CAC)</div>
              <div className="text-base font-bold text-emerald-900 tabular-nums">{fmtKc(costPerDeal)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 30denní delta badge — vždy porovnává POSLEDNÍCH 30 DNÍ vs PŘEDCHOZÍCH 30 DNÍ.
//   value     = součet za posledních 30 dní
//   prev      = součet za předchozích 30 dní (60–30 dní zpět)
//   direction = "up" (víc je lépe), "down" (míň je lépe). Default "up".
//   snapshot  = true → zobraz neutrální "Aktuální stav" pill bez čísla (pro tiles bez historie)
//
// Růst se počítá jako (value - prev) / prev × 100 — to je SKUTEČNÝ růst, ne poměr.
function Delta30Badge({ value, prev, direction = "up", snapshot = false, label = "posl. 30 dní" }) {
  if (snapshot) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
        <Clock className="w-3 h-3" />
        Aktuální stav (bez historie)
      </span>
    );
  }
  const v = Number(value) || 0;
  const p = Number(prev) || 0;
  // Skutečný růst vs předchozích 30 dní
  let growthPct = null;
  let growthDirection = "neutral"; // "up" = roste, "down" = klesá
  if (p > 0) {
    growthPct = ((v - p) / p) * 100;
    if (growthPct > 1) growthDirection = "up";
    else if (growthPct < -1) growthDirection = "down";
  } else if (v > 0) {
    growthDirection = "up"; // z nuly nahoru
  }
  const isGoodGrowth = (direction === "up" && growthDirection === "up") || (direction === "down" && growthDirection === "down");
  const isBadGrowth = (direction === "up" && growthDirection === "down") || (direction === "down" && growthDirection === "up");
  const status = isGoodGrowth ? "good" : isBadGrowth ? "bad" : "neutral";
  const colors = {
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    bad: "bg-rose-50 text-rose-700 border-rose-200",
    neutral: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const Icon = status === "good" ? TrendingUp : status === "bad" ? TrendingDown : ArrowRight;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold border tabular-nums ${colors[status]}`}
      title={`Posledních 30 dní: ${fmtInt(v)}. Předchozích 30 dní (60–30 dní zpět): ${fmtInt(p)}`}
    >
      <Icon className="w-3 h-3" />
      {fmtInt(v)} <span className="font-normal opacity-75 normal-case">{label}</span>
      {growthPct != null && (
        <span className={`font-bold ${status === "good" ? "text-emerald-800" : status === "bad" ? "text-rose-800" : "text-slate-700"}`}>
          {growthPct >= 0 ? "+" : ""}{growthPct.toFixed(0)} % vs. předchozích 30
        </span>
      )}
      {growthPct == null && p === 0 && v > 0 && (
        <span className="font-normal opacity-75">(předchozích 30 dní bez dat)</span>
      )}
    </span>
  );
}

// === KPI dlaždice ve stylu Stats — jednotná, čistá ===
// delta30d: { value, prev, direction, snapshot, label } — viz Delta30Badge
function KpiTile({ icon: Icon, label, value, before, suffix = "", betterDirection = "up", accent = "violet", tip, subtitle = null, delta30d = null }) {
  const accents = {
    violet: { bg: "bg-brand-50", iconBg: "bg-brand-100 text-brand-700", border: "border-brand-200" },
    pink: { bg: "bg-pink-50", iconBg: "bg-pink-100 text-pink-700", border: "border-pink-200" },
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100 text-blue-700", border: "border-blue-200" },
    emerald: { bg: "bg-emerald-50", iconBg: "bg-emerald-100 text-emerald-700", border: "border-emerald-200" },
    amber: { bg: "bg-amber-50", iconBg: "bg-amber-100 text-amber-700", border: "border-amber-200" },
    indigo: { bg: "bg-plum-50", iconBg: "bg-plum-100 text-plum-700", border: "border-plum-200" },
  };
  const a = accents[accent] || accents.violet;
  const pruhy = {
    violet: "bg-brand", pink: "bg-pink-500", blue: "bg-sky-500",
    emerald: "bg-emerald-500", amber: "bg-amber-500", indigo: "bg-plum-500",
  };
  const change = pctChange(before, value);
  const isPos = (betterDirection === "up" && change > 0) || (betterDirection === "down" && change < 0);
  const meaningful = Math.abs(change) >= 5;
  const status = meaningful ? (isPos ? "good" : "bad") : "neutral";
  const deltaColors = { good: "bg-emerald-100 text-emerald-700", bad: "bg-rose-100 text-rose-700", neutral: "bg-slate-100 text-slate-500" };
  const DeltaIcon = status === "good" ? TrendingUp : status === "bad" ? TrendingDown : ArrowRight;
  const showBeforeBlock = Number(before) > 0;

  // 13.9.2026: dlaždice má stejný tvar jako čísla na ostatních přehledech,
  // barevný proužek nahoře místo barevného rámečku a kolečka s ikonou.
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glass">
      <div className={`h-1.5 ${pruhy[accent] || pruhy.violet}`} />
      <div className="p-3">
        <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <span className="min-w-0 truncate">{label}</span>
          {tip && <InfoTip text={tip} />}
        </div>
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-xl font-bold tabular-nums text-slate-900 sm:text-2xl">{fmtInt(value)}</span>
          {suffix && <span className="text-sm font-semibold text-slate-500">{suffix}</span>}
        </div>
        {subtitle && <div className="mt-1 text-xs leading-snug text-slate-500">{subtitle}</div>}
        {(delta30d || showBeforeBlock) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {delta30d && <Delta30Badge {...delta30d} />}
            {showBeforeBlock && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${deltaColors[status]}`}>
                <DeltaIcon className="h-3 w-3" />{change >= 0 ? "+" : ""}{change.toFixed(0)} % proti {fmtInt(before)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Štítek datového zdroje:
//  - kind="live"   → zelená "ŽIVÁ DATA" (auto-aktualizace z API, cache 6 h)
//  - kind="import" → oranžová "MANUÁLNÍ CSV IMPORT" + datum posledního importu
function DataSourceBadge({ kind, lastDate, firstDate }) {
  if (kind === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Živá data
      </span>
    );
  }
  // import
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-200">
      <RefreshCw className="w-3 h-3" />
      Manuální CSV import
      {lastDate && (
        <span className="normal-case tracking-normal font-semibold text-amber-900">
          do {fmtDateCz(lastDate)}
        </span>
      )}
    </span>
  );
}

// Karta s grafem. Tmavá hlavička jako ostatní karty, uvnitř samotný graf.
function KartaGrafu({ ikona, nazev, popis, children }) {
  return (
    <KartaPrehledu>
      <HlavickaDlazdice ikona={ikona} nazev={nazev} podtitulek={popis} />
      <div className="p-3 sm:p-4">{children}</div>
    </KartaPrehledu>
  );
}

// 13.9.2026: sekce dělí světlý pruh přes celou šířku, stejný jako skupiny uvnitř
// karet a jako sekce v ročním přehledu. Dřív to byl nadpis s kolečkem a čárou.
// Delší vysvětlení sekce se přesunulo pod otazník, ať pruh drží jednu výšku.
function SectionHeader({ icon: Icon, title, subtitle, badge, dataSource }) {
  return (
    <div className="-mx-4 mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 bg-brand-light px-4 py-2.5 sm:-mx-5 sm:px-5">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-brand-deep" />}
      <span className="text-xs font-bold uppercase tracking-wide text-brand-950">{title}</span>
      {subtitle && <InfoTip text={typeof subtitle === "string" ? subtitle : ""} />}
      {badge && (
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brand-deep">{badge.text}</span>
      )}
      {dataSource && <span className="ml-auto shrink-0">{dataSource}</span>}
    </div>
  );
}

// Agreguje denní data do týdenních (méně zubaté křivky, čitelnější trend)
function aggregateToWeekly(timeseries) {
  if (!timeseries?.length) return [];
  const weeks = {};
  timeseries.forEach((d) => {
    const date = new Date(d.date + "T00:00:00Z");
    // ISO week start (pondělí)
    const day = date.getUTCDay() || 7;
    const monday = new Date(date);
    monday.setUTCDate(date.getUTCDate() - (day - 1));
    const wk = monday.toISOString().substring(0, 10);
    if (!weeks[wk]) weeks[wk] = { week_start: wk, reach: 0, engagement: 0, page_visits: 0, link_clicks: 0, new_followers: 0, impressions: 0, days: 0 };
    weeks[wk].reach += d.reach || 0;
    weeks[wk].engagement += d.engagement || 0;
    weeks[wk].page_visits += d.page_visits || 0;
    weeks[wk].link_clicks += d.link_clicks || 0;
    weeks[wk].new_followers += d.new_followers || 0;
    weeks[wk].impressions += d.impressions || 0;
    weeks[wk].days++;
  });
  return Object.values(weeks).sort((a, b) => a.week_start.localeCompare(b.week_start));
}

// Generický single-metric graf — větší, čitelnější, area chart s gradientem
function MetricTrendChart({ timeseries, metricKey, metricLabel, color, collabStartISO, height = 220 }) {
  const weekly = aggregateToWeekly(timeseries);
  if (!weekly.length) return <div className="text-sm text-slate-500 p-6 text-center">Žádná data.</div>;
  const data = weekly.map((w) => ({
    week: fmtRelDate(w.week_start),
    week_iso: w.week_start,
    value: w[metricKey],
  }));
  // Najdi index týdne kdy začala spolupráce
  const collabIdx = collabStartISO ? data.findIndex((d) => d.week_iso >= collabStartISO) : -1;
  const refLabel = collabIdx >= 0 ? data[collabIdx].week : null;
  const gradId = `grad-${metricKey}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 36, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.45} />
            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
        <XAxis dataKey="week" tick={{ fontSize: 11 }} interval={1} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
        <Tooltip
          formatter={(v) => [fmtInt(v), metricLabel]}
          labelFormatter={(label) => `Týden od ${label}`}
        />
        {refLabel && (
          <ReferenceLine
            x={refLabel}
            stroke="#dc2626"
            strokeDasharray="4 2"
            label={({ viewBox }) => (
              <g transform={`translate(${viewBox.x}, 8)`}>
                <rect x={-58} y={0} width={116} height={20} fill="#ffffff" stroke="#fecaca" rx={4} />
                <text x={0} y={14} fill="#dc2626" fontSize={11} fontWeight={700} textAnchor="middle">Start spolupráce</text>
              </g>
            )}
          />
        )}
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#${gradId})`} name={metricLabel} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Top list — řazený seznam (top stránky / klíčová slova) s primární a sekundární metrikou.
// Vykresluje barvy proporcionálně k primární metrice (max width = 100%).
function TopList({ icon: Icon, title, subtitle, rows, labelKey, mapLabel, primaryKey, primaryLabel, secondaryKey, secondaryLabel }) {
  const items = (rows || []).filter((r) => r && r[primaryKey] > 0);
  const max = items.reduce((m, r) => Math.max(m, Number(r[primaryKey]) || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-brand-600" />}
            {title}
          </div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <div className="text-xs text-slate-400">{items.length} pol.</div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 text-center">Čeká na data, Search Console zatím nevrátila výsledky.</div>
      ) : (
        <div className="space-y-2">
          {items.map((row, i) => {
            const rawLabel = row[labelKey] || "—";
            const label = mapLabel ? mapLabel(rawLabel) : rawLabel;
            const primary = Number(row[primaryKey]) || 0;
            const secondary = Number(row[secondaryKey]) || 0;
            const pct = max > 0 ? Math.round((primary / max) * 100) : 0;
            return (
              <div key={i} className="group">
                <div className="flex items-baseline gap-3 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate" title={rawLabel}>{label}</div>
                  </div>
                  <div className="text-base font-bold text-brand-700 tabular-nums whitespace-nowrap">{primary.toLocaleString("cs-CZ")} <span className="text-xs font-normal text-slate-400">{primaryLabel}</span></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-brand-400 to-brand-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{secondary.toLocaleString("cs-CZ")} {secondaryLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Web traffic graf — měsíční sessions z GA4
function WebTrafficChart({ trend, collabStartISO }) {
  if (!trend?.length) return null;
  const data = trend.map((t) => {
    const m = t.month;
    const formatted = m.length === 6 ? `${m.substring(0, 4)}-${m.substring(4, 6)}` : m;
    return { month: formatted, sessions: t.sessions, users: t.users };
  });
  const collabMonth = collabStartISO ? collabStartISO.substring(0, 7) : null;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={data} margin={{ top: 36, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="webSessG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6E519F" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#6E519F" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip formatter={(v) => fmtInt(v)} />
        <Legend />
        {collabMonth && (
          <ReferenceLine
            x={collabMonth}
            stroke="#dc2626"
            strokeDasharray="4 2"
            label={({ viewBox }) => (
              <g transform={`translate(${viewBox.x}, 8)`}>
                <rect x={-58} y={0} width={116} height={20} fill="#ffffff" stroke="#fecaca" rx={4} />
                <text x={0} y={14} fill="#dc2626" fontSize={11} fontWeight={700} textAnchor="middle">Start spolupráce</text>
              </g>
            )}
          />
        )}
        <Area type="monotone" dataKey="sessions" stroke="#6E519F" strokeWidth={2.5} fill="url(#webSessG)" name="Návštěvy webu" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function LeadsTable({ leads, title }) {
  const stageColor = (cls) => ({
    deal: "bg-emerald-100 text-emerald-700",
    loss: "bg-rose-100 text-rose-700",
    pending: "bg-amber-100 text-amber-700",
    nq: "bg-slate-100 text-slate-500",
  }[cls] || "bg-slate-100 text-slate-500");
  const stageLabel = (cls) => ({
    deal: "Uzavřeno",
    loss: "Prohra",
    pending: "Čeká",
    nq: "Nekvalitní",
  }[cls] || cls);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
      <div className="text-base font-bold text-slate-900 mb-3">{title}</div>
      {leads?.length > 0 ? (
        <div className="space-y-1.5">
          {leads.map((l, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <div className="text-xs text-slate-400 tabular-nums w-16 flex-shrink-0">{fmtRelDate(l.date)}</div>
                <div className="text-sm text-slate-800 font-semibold truncate demo-sensitive">{l.name || "(bez jména)"}</div>
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg ${stageColor(l.classification)}`}>
                {stageLabel(l.classification)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-500 italic py-6 text-center">Žádné SM Organic leady za toto období.</div>
      )}
    </div>
  );
}

function FbPostsTimeline({ posts }) {
  // Posty řadíme podle data (DESC) — nejnovější nahoře. Per-post engagement (lajky / komentáře)
  // Meta API blokuje #10 (chybí pages_read_engagement permission a App Review). Pokud máme
  // engagement čísla (po případném budoucím povolení scopu), zobrazíme je. Jinak skryto.
  const sorted = [...(posts || [])].sort(
    (a, b) => new Date(b.created_time).getTime() - new Date(a.created_time).getTime()
  ).slice(0, 10);
  const hasEngagement = sorted.some((p) => (p.likes || 0) > 0 || (p.comments || 0) > 0 || (p.shares || 0) > 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center"><Facebook className="w-4 h-4" /></div>
          <div>
            <div className="text-base font-bold text-slate-900">Posledních 10 Facebook příspěvků</div>
            <div className="text-xs text-slate-500">Chronologicky (nejnovější nahoře). Klikni na příspěvek pro detail na FB.</div>
          </div>
        </div>
        <InfoTip text="Seznam posledních příspěvků chronologicky. Engagement metriky (lajky, komentáře, sdílení) per post Meta API blokuje bez schválené App Review (chyba #10, chybí pages_read_engagement). Souhrnný engagement za celé období vidíš v dlaždicích nahoře (z CSV exportu Business Manageru)." />
      </div>
      {sorted.length > 0 ? (
        <div className="space-y-2 max-h-[520px] overflow-y-auto">
          {sorted.map((p, i) => {
            const likes = p.likes || 0;
            const comments = p.comments || 0;
            const shares = p.shares || 0;
            return (
              <a key={i} href={p.permalink} target="_blank" rel="noreferrer" className="block p-3 rounded-xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50/40 transition-colors group">
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-semibold text-slate-500 w-12">{fmtRelDate(p.created_time)}</div>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-semibold text-slate-700 flex-shrink-0">
                    {hasEngagement && (
                      <>
                        <span className="flex items-center gap-1" title="Lajky / reakce"><ThumbsUp className="w-3.5 h-3.5 text-blue-600" /> {fmtInt(likes)}</span>
                        <span className="flex items-center gap-1" title="Komentáře"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> {fmtInt(comments)}</span>
                        {shares > 0 && <span className="flex items-center gap-1" title="Sdílení"><ArrowRight className="w-3.5 h-3.5 text-brand-600" /> {fmtInt(shares)}</span>}
                      </>
                    )}
                    <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-600" />
                  </div>
                </div>
                <div className="text-sm text-slate-800 leading-snug line-clamp-2 demo-sensitive">{p.message || "(bez popisku)"}</div>
              </a>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-slate-500 italic py-4 text-center">Žádné příspěvky.</div>
      )}
      {!hasEngagement && sorted.length > 0 && (
        <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 leading-relaxed">
          <b>Proč u postů nejsou lajky a komentáře?</b> Meta Graph API blokuje per-post engagement metriky bez schválené App Review (chyba #10, chybí scope <code className="bg-slate-200 px-1 rounded">pages_read_engagement</code>). Souhrnný engagement za období je vidět v dlaždicích nahoře z CSV exportu (Interakce, Klikání, Reach). Pro detail klikni na příspěvek a otevře se přímo na FB.
        </div>
      )}
    </div>
  );
}

export default function Marketing() {
  const { data, loading, error, refetch, refreshing } = useApiData("/api/data-marketing");
  const { blurred } = useDemoMode();
  // Toggle pro skrytí verdictu (užitečné při screen sharing s SM specialistkou)
  // Persistuje v localStorage, default = zobrazit
  const [verdictHidden, setVerdictHidden] = useState(() => {
    try { return localStorage.getItem("fw40_marketing_verdict_hidden") === "1"; }
    catch { return false; }
  });
  const toggleVerdict = () => {
    setVerdictHidden((v) => {
      const next = !v;
      try { localStorage.setItem("fw40_marketing_verdict_hidden", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // 30.6.2026 feedback #24: rozsah nahraných CSV dat čteme z lehkého GET /api/upload-meta-history
  // (autoritativní Blob). Nezávislé na 6h cache /api/data-marketing — její force=1 refresh padá
  // na ~30s timeout, takže banner po uploadu reportu ukazoval starý rozsah dat.
  const [csvRanges, setCsvRanges] = useState(null);
  const loadCsvRanges = async () => {
    try {
      const r = await apiFetch("/api/upload-meta-history");
      const j = await r.json();
      if (j?.ok && j.ranges) setCsvRanges(j.ranges);
    } catch { /* tichý fallback na rozsah z data-marketingu */ }
  };
  useEffect(() => { loadCsvRanges(); }, []);

  if (loading && !data) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <div className="h-48 bg-slate-100 rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-44 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      </div>
    );
  }
  if (error || !data?.ok) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-rose-900">
          <div className="font-bold mb-1">Chyba načítání</div>
          <div className="text-sm">{error?.message || data?.error || "Neznámá chyba"}</div>
        </div>
      </div>
    );
  }

  const { ga4, gsc, period, verdict, property, meta, roi } = data;
  const csvBefore = meta?.csv_before?.per_month || {};
  const csvDuring = meta?.csv_during?.per_month || {};
  const csvTimeseries = meta?.csv_timeseries || [];
  // Datum poslední dostupné CSV položky (od kdy aktualizovat).
  // Preferuje čerstvý rozsah z Blobs (csvRanges, feedback #24), fallback na data-marketing cache.
  const csvLastDate = csvRanges?.fb?.to || (csvTimeseries.length > 0 ? csvTimeseries[csvTimeseries.length - 1].date : null);
  const csvFirstDate = csvRanges?.fb?.from || (csvTimeseries.length > 0 ? csvTimeseries[0].date : null);
  const today = new Date().toISOString().substring(0, 10);
  const daysSinceLastUpdate = csvLastDate ? Math.floor((new Date(today) - new Date(csvLastDate)) / 86400000) : null;
  const igInfo = meta?.ig_info || {};
  const fbAggrDuring = ((meta?.fb_aggregates || {}).during || {});
  // Defenzivně: pokud agregát selhal (0), spadni na top_posts.length, případně na fb_top_posts_during.
  const fbTopDuring = (meta?.fb_top_posts_during || []);
  const fbPostsDuring = (fbAggrDuring.posts_count > 0)
    ? fbAggrDuring.posts_count
    : (Array.isArray(fbAggrDuring.top_posts) && fbAggrDuring.top_posts.length > 0
        ? fbAggrDuring.top_posts.length
        : fbTopDuring.length || 0);
  const smLeads = roi?.sm_organic_leads || {};
  const smBefore = smLeads.before || {};
  const smDuring = smLeads.during || {};
  // 30denní delty z backendu (data-marketing.js → meta.deltas_30d)
  const deltas30 = meta?.deltas_30d || {};
  const csvLast30 = deltas30.csv_last30 || {};
  const csvPrev30 = deltas30.csv_prev30 || {};
  // Instagram CSV agregát + 30denní delta
  const csvIgBefore = meta?.csv_ig_before?.per_month || {};
  const csvIgDuring = meta?.csv_ig_during?.per_month || {};
  const csvIgTimeseries = meta?.csv_ig_timeseries || [];
  const csvIgLastDate = csvRanges?.ig?.to || (csvIgTimeseries.length > 0 ? csvIgTimeseries[csvIgTimeseries.length - 1].date : null);
  const csvIgFirstDate = csvRanges?.ig?.from || (csvIgTimeseries.length > 0 ? csvIgTimeseries[0].date : null);
  const igDaysSinceLastUpdate = csvIgLastDate ? Math.floor((new Date(today) - new Date(csvIgLastDate)) / 86400000) : null;
  const csvIgLast30 = deltas30.csv_ig_last30 || {};
  const csvIgPrev30 = deltas30.csv_ig_prev30 || {};

  return (
    <Stranka className={blurred ? "demo-blur" : ""}>
      {blurred && (
        <style>{`
          .demo-blur .tabular-nums,
          .demo-blur .recharts-text,
          .demo-blur .recharts-cartesian-axis-tick-value,
          .demo-blur .recharts-tooltip-wrapper,
          .demo-blur .recharts-bar,
          .demo-blur .recharts-pie,
          .demo-blur .recharts-area,
          .demo-blur .recharts-line,
          .demo-blur td.font-medium,
          .demo-blur .demo-sensitive {
            filter: blur(8px);
            user-select: none;
          }
        `}</style>
      )}
      {/* === HLAVIČKA — barevný gradient ve stylu Total statistics === */}
      <GradientHeader
        title="Marketing report"
        icon={Megaphone}
        loading={refreshing}
        akce={[
          { klic: "obnovit", ikona: RefreshCw, popis: "Aktualizovat", onClick: () => { loadCsvRanges(); refetch?.(); }, disabled: refreshing },
        ]}
      />

      {/* 13.9.2026 (fáze 4f, rozhodnutí Č1): stav nahraných CSV dat je karta jako
          každá jiná. Dřív to byl velký barevný pruh s bílými kartami uvnitř a
          dvanácti odškrtávátky; teď je z každé platformy skupina se dvěma řádky
          a odškrtávátka nahradilo jedno číslo. Nahrávání CSV se kreslí až od
          počítače, na telefonu se soubory z Business Manageru stejně netahají. */}
      {(csvLastDate || csvIgLastDate) && (() => {
        const metriky = [
          { key: "reach", labelFb: "Diváci", labelIg: "Dosah" },
          { key: "impressions", labelFb: "Zobrazení", labelIg: "Zobrazení" },
          { key: "engagement", labelFb: "Interakce", labelIg: "Interakce" },
          { key: "link_clicks", labelFb: "Klikání odkaz", labelIg: "Klikání odkaz" },
          { key: "page_visits", labelFb: "Návštěvy", labelIg: "Návštěvy profilu" },
          { key: "new_followers", labelFb: "Sledování", labelIg: "Sledování" },
        ];
        const fbTotal = meta?.csv_during?.total || {};
        const igTotal = meta?.csv_ig_during?.total || {};
        const fbChybi = metriky.filter((m) => !((Number(fbTotal[m.key]) || 0) > 0));
        const igChybi = metriky.filter((m) => !((Number(igTotal[m.key]) || 0) > 0));
        const fbMa = metriky.length - fbChybi.length;
        const igMa = metriky.length - igChybi.length;
        const stari = (dni) => {
          if (dni == null) return null;
          if (dni <= 0) return "dnešní";
          return `${dni} ${dni === 1 ? "den" : dni < 5 ? "dny" : "dní"}`;
        };
        // Krátké datum bez roku, celý rozsah s rokem se do řádku na telefonu nevejde.
        const den = (iso) => {
          if (!iso) return "";
          const d = new Date(iso);
          return isNaN(d.getTime()) ? "" : `${d.getDate()}.${d.getMonth() + 1}.`;
        };
        const tonStari = (dni) => (dni != null && dni > 7 ? "text-amber-700" : "text-slate-900");
        const napovedaMetrik = "Metrika se počítá jako nahraná, když má za sledované období nenulový součet. Facebook: Diváci, Zobrazení, Interakce, Klikání odkaz, Návštěvy, Sledování. Instagram: Dosah, Zobrazení, Interakce, Klikání odkaz, Návštěvy profilu, Sledování.";

        return (
          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={FileText}
              nazev="Business Manager"
              podtitulek="data se nahrávají ručně z CSV"
              odznak={`${fbMa + igMa} z 12`}
              odznakTon={fbMa + igMa === 12 ? "bily" : "zluty"}
              napoveda="Aktualizace znamená stáhnout CSV pro obě platformy zvlášť. V Business Manageru v Insights vyexportuj šest metrik za Facebook a šest za Instagram, pak vyber všech dvanáct souborů naráz. Po výběru se otevře kontrola s možností opravit rozpoznání FB a IG."
            />

            <SkupinaPrehledu ikona={Facebook} nazev="Facebook" />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Období dat"
                hodnota={csvLastDate ? `${den(csvFirstDate)} až ${den(csvLastDate)}` : "žádná data"}
                doplnek={stari(daysSinceLastUpdate)}
                tonHodnoty={tonStari(daysSinceLastUpdate)}
              />
              <RadekPrehledu
                popisek="Nahrané metriky"
                napoveda={fbChybi.length > 0 ? `Chybí: ${fbChybi.map((m) => m.labelFb).join(", ")}. ${napovedaMetrik}` : napovedaMetrik}
                hodnota={`${fbMa} z 6`}
                tonHodnoty={fbMa === 6 ? "text-emerald-700" : fbMa === 0 ? "text-rose-700" : "text-amber-700"}
              />
            </RadkyPrehledu>

            <SkupinaPrehledu ikona={Instagram} nazev="Instagram" />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Období dat"
                hodnota={csvIgLastDate ? `${den(csvIgFirstDate)} až ${den(csvIgLastDate)}` : "žádná data"}
                doplnek={stari(igDaysSinceLastUpdate)}
                tonHodnoty={tonStari(igDaysSinceLastUpdate)}
              />
              <RadekPrehledu
                popisek="Nahrané metriky"
                napoveda={igChybi.length > 0 ? `Chybí: ${igChybi.map((m) => m.labelIg).join(", ")}. ${napovedaMetrik}` : napovedaMetrik}
                hodnota={`${igMa} z 6`}
                tonHodnoty={igMa === 6 ? "text-emerald-700" : igMa === 0 ? "text-rose-700" : "text-amber-700"}
              />
            </RadkyPrehledu>

            {/* Nahrávání jen na počítači, na telefonu se CSV z Business Manageru netahá. */}
            <div className="hidden p-4 lg:block">
              <UploadBox onUploaded={() => { loadCsvRanges(); refetch?.(); }} />
            </div>
          </KartaPrehledu>
        );
      })()}

      {/* === HLAVNÍ VERDIKT (lze skrýt např. při screen-sharing) === */}
      <div className="relative">
        <div className="flex justify-end mb-2">
          <button
            onClick={toggleVerdict}
            className="btn btn-secondary"
            title={verdictHidden ? "Zobrazit verdikt" : "Skrýt verdikt (např. při screen-sharing)"}
          >
            {verdictHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {verdictHidden ? "Zobrazit verdikt" : "Skrýt verdikt"}
          </button>
        </div>
        {!verdictHidden && (
          <VerdictHero verdict={verdict} period={period} />
        )}
        {verdictHidden && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-4 text-center text-sm text-slate-500">
            Verdikt je skrytý. Klikni na <b>Zobrazit verdikt</b> nahoře pro odkrytí.
          </div>
        )}
      </div>

      {/* === ROI PANEL === */}
      <RoiPanel roi={roi} period={period} smLeadsDuring={smDuring} />

      {/* === META FACEBOOK STATS Z CSV (Business Manager export) === */}
      <div>
        <SectionHeader
          icon={Sparkles}
          title="Facebook výkonnost (data z Business Manageru)"
          subtitle="Vše jako PRŮMĚR ZA MĚSÍC: leden 2026 (před) vs únor až duben 2026 (během). Tím lze fér porovnat 1 měsíc proti 3 měsícům."
          badge={{ text: "Primární", type: "primary" }}
          dataSource={<DataSourceBadge kind="import" lastDate={meta?.csv_last_date} firstDate={meta?.csv_first_date} />}
        />
        <div className="mb-3 p-3 bg-brand-50 border border-brand-200 rounded-xl text-xs text-brand-900 leading-relaxed">
          <b>Jak číst tato čísla:</b> "Před" = leden 2026 (1 měsíc, před nástupem specialistky). "Nyní" = průměr za jeden měsíc během spolupráce (únor až duben 2026, vyděleno počtem měsíců). Bez tohoto přepočtu by srovnání bylo nefér: 3 měsíce vždy přinesou víc absolutního čísla než 1 měsíc, i kdyby se nic nezlepšilo.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiTile
            icon={Eye} label="Reach (Diváci) / měs"
            value={csvDuring.reach} before={csvBefore.reach} accent="violet"
            subtitle="Kolik unikátních lidí vidí obsah" tip="Počet unikátních uživatelů, kterým se zobrazil obsah Facebook stránky. Hlavní indikátor jak algoritmus pouští content."
            delta30d={{ value: csvLast30.reach, prev: csvPrev30.reach, direction: "up" }}
          />
          <KpiTile
            icon={BarChart3} label="Zobrazení / měs"
            value={csvDuring.impressions} before={csvBefore.impressions} accent="indigo"
            subtitle="Celkový počet zobrazení obsahu" tip="Kolikrát se obsah zobrazil dohromady (1 člověk může vidět vícekrát)."
            delta30d={{ value: csvLast30.impressions, prev: csvPrev30.impressions, direction: "up" }}
          />
          <KpiTile
            icon={Heart} label="Interakce / měs"
            value={csvDuring.engagement} before={csvBefore.engagement} accent="pink"
            subtitle="Lajky + komentáře + sdílení + reakce" tip="Hlavní indikátor kvality obsahu, jak lidi reagují."
            delta30d={{ value: csvLast30.engagement, prev: csvPrev30.engagement, direction: "up" }}
          />
          <KpiTile
            icon={MousePointerClick} label="Klikání odkaz / měs"
            value={csvDuring.link_clicks} before={csvBefore.link_clicks} accent="blue"
            subtitle="Z FB Page na web nebo bio link" tip="Konverze ze SM do akce (otevření webu z bio nebo postu)."
            delta30d={{ value: csvLast30.link_clicks, prev: csvPrev30.link_clicks, direction: "up" }}
          />
          <KpiTile
            icon={Globe} label="Návštěvy FB profilu / měs"
            value={csvDuring.page_visits} before={csvBefore.page_visits} accent="emerald"
            subtitle="Kolik lidí navštívilo Page" tip="Zájem o značku, kolik lidí si proklikalo profil."
            delta30d={{ value: csvLast30.page_visits, prev: csvPrev30.page_visits, direction: "up" }}
          />
          <KpiTile
            icon={Users} label="Noví fans / měs"
            value={csvDuring.new_followers} before={csvBefore.new_followers} accent="amber"
            subtitle="Kolik nových sledujících za období" tip="Dlouhodobý dopad, kolik lidí se přihlásilo k odběru obsahu."
            delta30d={{ value: csvLast30.new_followers, prev: csvPrev30.new_followers, direction: "up" }}
          />
        </div>

        {/* === 4 separátní grafy — každá metrika zvlášť === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
          <KartaGrafu ikona={Eye} nazev="Reach (Diváci)" popis="Týdenní součet, kolik unikátních lidí vidělo obsah">
<MetricTrendChart timeseries={csvTimeseries} metricKey="reach" metricLabel="Reach" color="#7C5DB0" collabStartISO={period?.collab_start} />
          </KartaGrafu>

          <KartaGrafu ikona={Heart} nazev="Interakce (lajky + komentáře)" popis="Týdenní součet, kvalita obsahu">
<MetricTrendChart timeseries={csvTimeseries} metricKey="engagement" metricLabel="Interakce" color="#ec4899" collabStartISO={period?.collab_start} />
          </KartaGrafu>

          <KartaGrafu ikona={Users} nazev="Noví fans" popis="Týdenní součet, dlouhodobý dopad">
<MetricTrendChart timeseries={csvTimeseries} metricKey="new_followers" metricLabel="Noví fans" color="#f59e0b" collabStartISO={period?.collab_start} />
          </KartaGrafu>

          <KartaGrafu ikona={MousePointerClick} nazev="Klikání na odkaz" popis="Týdenní součet, kolik lidí kliklo na bio link">
<MetricTrendChart timeseries={csvTimeseries} metricKey="link_clicks" metricLabel="Klikání" color="#3b82f6" collabStartISO={period?.collab_start} />
          </KartaGrafu>
        </div>
      </div>

      {/* === META INSTAGRAM STATS Z CSV === */}
      <div>
        <SectionHeader
          icon={Instagram}
          title="Instagram výkonnost (data z Business Manageru)"
          subtitle="Stejná logika porovnání: leden 2026 vs únor až duben 2026 (vždy průměr/měsíc)."
          badge={{ text: "Primární", type: "primary" }}
          dataSource={<DataSourceBadge kind="import" lastDate={meta?.csv_ig_last_date} firstDate={meta?.csv_ig_first_date} />}
        />
        {csvIgTimeseries.length === 0 ? (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-900">
            Zatím žádná IG data. Stáhni z Business Manageru → Insights → Instagram → exportuj 5 metrik (Dosah, Interakce s obsahem, Kliknutí na odkaz na Instagramu, Návštěvy instagramového profilu, Sledování na Instagramu) a nahraj tlačítkem nahoře. Platforma se detekuje automaticky z titulu CSV.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <KpiTile
                icon={Eye} label="Dosah / měs"
                value={csvIgDuring.reach} before={csvIgBefore.reach} accent="pink"
                subtitle="Kolik unikátních lidí vidí IG obsah" tip="Dosah na Instagramu, počet unikátních uživatelů, kteří viděli příspěvek nebo story."
                delta30d={{ value: csvIgLast30.reach, prev: csvIgPrev30.reach, direction: "up" }}
              />
              <KpiTile
                icon={BarChart3} label="Zobrazení / měs"
                value={csvIgDuring.impressions} before={csvIgBefore.impressions} accent="pink"
                subtitle="Celkový počet zobrazení IG obsahu" tip="Kolikrát se IG obsah zobrazil dohromady (1 člověk může vidět vícekrát)."
                delta30d={{ value: csvIgLast30.impressions, prev: csvIgPrev30.impressions, direction: "up" }}
              />
              <KpiTile
                icon={Heart} label="Interakce / měs"
                value={csvIgDuring.engagement} before={csvIgBefore.engagement} accent="pink"
                subtitle="Lajky + komentáře + uložení + sdílení" tip="Hlavní indikátor kvality obsahu na IG."
                delta30d={{ value: csvIgLast30.engagement, prev: csvIgPrev30.engagement, direction: "up" }}
              />
              <KpiTile
                icon={MousePointerClick} label="Klikání odkaz / měs"
                value={csvIgDuring.link_clicks} before={csvIgBefore.link_clicks} accent="pink"
                subtitle="Kliknutí na odkaz v IG bio" tip="Konverze z Instagramu na web (klik z bio link)."
                delta30d={{ value: csvIgLast30.link_clicks, prev: csvIgPrev30.link_clicks, direction: "up" }}
              />
              <KpiTile
                icon={Globe} label="Návštěvy profilu / měs"
                value={csvIgDuring.page_visits} before={csvIgBefore.page_visits} accent="pink"
                subtitle="Kolik lidí navštívilo IG profil" tip="Zájem o značku, kolik lidí proklikalo IG profil."
                delta30d={{ value: csvIgLast30.page_visits, prev: csvIgPrev30.page_visits, direction: "up" }}
              />
              <KpiTile
                icon={Users} label="Noví sledující / měs"
                value={csvIgDuring.new_followers} before={csvIgBefore.new_followers} accent="pink"
                subtitle="Kolik nových IG followers za období" tip="Dlouhodobý dopad, kolik lidí se přihlásilo k odběru IG profilu."
                delta30d={{ value: csvIgLast30.new_followers, prev: csvIgPrev30.new_followers, direction: "up" }}
              />
            </div>

            {/* Grafy IG — 4 separátní */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
              <KartaGrafu ikona={Eye} nazev="Dosah na IG" popis="Týdenní součet, kolik unikátních lidí vidělo IG obsah">
<MetricTrendChart timeseries={csvIgTimeseries} metricKey="reach" metricLabel="Dosah" color="#ec4899" collabStartISO={period?.collab_start} />
              </KartaGrafu>
              <KartaGrafu ikona={Heart} nazev="Interakce na IG" popis="Týdenní součet, lajky, komentáře, uložení">
<MetricTrendChart timeseries={csvIgTimeseries} metricKey="engagement" metricLabel="Interakce" color="#db2777" collabStartISO={period?.collab_start} />
              </KartaGrafu>
              <KartaGrafu ikona={Users} nazev="Noví IG sledující" popis="Týdenní přírůstek followers">
<MetricTrendChart timeseries={csvIgTimeseries} metricKey="new_followers" metricLabel="Noví fans" color="#f59e0b" collabStartISO={period?.collab_start} />
              </KartaGrafu>
              <KartaGrafu ikona={MousePointerClick} nazev="Klikání odkaz IG" popis="Týdenní součet, kolik lidí kliklo na bio link">
<MetricTrendChart timeseries={csvIgTimeseries} metricKey="link_clicks" metricLabel="Klikání" color="#7C5DB0" collabStartISO={period?.collab_start} />
              </KartaGrafu>
            </div>
          </>
        )}
      </div>

      {/* === SM ORGANIC LEADY === */}
      <div>
        <SectionHeader
          icon={Target}
          title="Leady přivedené přes SM Organic"
          subtitle="Kolik kontaktů přinesla specialistka a kolik z nich se uzavřelo jako klient"
          badge={{ text: "Klíčové pro ROI", type: "accent" }}
          dataSource={<DataSourceBadge kind="live" />}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LeadsTable
            title={`Před spoluprací (1. 1. – 31. 1. 2026): ${smBefore.total || 0} ${(smBefore.total || 0) === 1 ? "lead" : "leadů"}`}
            leads={smBefore.leads || []}
          />
          <LeadsTable
            title={`Během spolupráce (od 1. 2. 2026): ${smDuring.total || 0} ${(smDuring.total || 0) === 1 ? "lead" : "leadů"}`}
            leads={smDuring.leads || []}
          />
        </div>
      </div>

      {/* === IG + FB AKTIVITA === */}
      <div>
        <SectionHeader
          icon={Instagram}
          title="Aktivita na sociálních sítích"
          subtitle="Followers, počet postů, co specialistka reálně dělá"
          dataSource={<DataSourceBadge kind="live" />}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiTile
            icon={Instagram} label="IG followers"
            value={igInfo.followers || 0} before={0} accent="pink"
            subtitle="Aktuální počet sledujících" tip="Aktuální stav z Meta API. 30denní přírůstek je sečtený nárůst sledujících z Business Manager CSV exportu (sloupec Sledování na Instagramu)."
            delta30d={{ value: csvIgLast30.new_followers, prev: csvIgPrev30.new_followers, direction: "up", label: "30 dní" }}
          />
          <KpiTile
            icon={Facebook} label="FB fans"
            value={(meta?.fb_page_insights_during || {}).current_fans || 0} before={0} accent="blue"
            subtitle="Aktuální počet fans" tip="Aktuální stav Facebook stránky. 30denní přírůstek je sečtený nárůst fans z Business Manager CSV exportu (sloupec Sledování)."
            delta30d={{ value: csvLast30.new_followers, prev: csvPrev30.new_followers, direction: "up", label: "30 dní" }}
          />
          <KpiTile
            icon={Calendar} label="FB postů za období"
            value={fbPostsDuring} before={0} accent="indigo"
            subtitle={`Frekvence aktivity (${period?.during?.months ? Math.round(fbPostsDuring / period.during.months) : 0}/měsíc)`}
            tip="Kolik příspěvků specialistka publikovala na Facebook za období spolupráce. 30denní přírůstek = počet postů publikovaných v posledních 30 dnech vs předchozích 30."
            delta30d={{ value: deltas30.fb_posts_last30, prev: deltas30.fb_posts_prev30, direction: "up", label: "30 dní" }}
          />
          {/* IG aktivita za 30 dní — počet aktivních dnů z CSV (proxy frekvence publikování).
              Meta blokuje per-post IG media list (#10), takže reálný post count nelze. Aktivní den = den
              kdy IG Dosah nebo Interakce > 0 (specialistka něco publikovala / engagement běžel). */}
          {(igInfo.media_count || 0) > 0 && (
            <KpiTile
              icon={Instagram} label="IG aktivních dnů"
              value={deltas30.ig_active_days_last30 || 0} before={0} accent="pink"
              suffix="/ 30 dní"
              subtitle={`Celkem postů na profilu: ${fmtInt(igInfo.media_count)} (lifetime)`}
              tip="Počet dnů v posledních 30 kdy IG profil reálně běžel, buď byl publikován příspěvek nebo proběhl engagement (Dosah/Interakce > 0). Odvozeno z CSV exportu Business Manageru. Meta blokuje individuální IG posty (#10), takže přesný post count nelze, ale aktivní dny jsou dobrá proxy frekvence publikování."
              delta30d={{ value: deltas30.ig_active_days_last30, prev: deltas30.ig_active_days_prev30, direction: "up", label: "30 dní" }}
            />
          )}
        </div>
        {/* Sekce posledních FB příspěvků: zobrazit JEN pokud máme reálná engagement data.
            Per-post lajky/komentáře blokuje Meta API #10 (chybí pages_read_engagement scope, vyžaduje App Review).
            Bez čísel by sekce byla neinformativní → schováváme. Souhrnný engagement je v dlaždicích nahoře z CSV. */}
        {(() => {
          const tp = fbAggrDuring.top_posts || [];
          const hasEng = tp.some((p) => (p.likes || 0) > 0 || (p.comments || 0) > 0 || (p.shares || 0) > 0);
          return hasEng ? <FbPostsTimeline posts={tp} /> : null;
        })()}
      </div>

      {/* === WEB DATA === */}
      <div>
        <SectionHeader
          icon={Globe}
          title="Vliv na web (kontextová data)"
          subtitle="Jestli SM přivádí lidi na ladislavzatecka.cz"
          badge={{ text: "Sekundární", type: "secondary" }}
          dataSource={<DataSourceBadge kind="live" />}
        />

        {/* Měsíc — porovnání před vs po startu spolupráce (per-měsíční průměr) */}
        <div className="text-sm font-semibold text-slate-700 mb-2 mt-1">Per-měsíční průměr (od startu spolupráce vs. před)</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiTile icon={Activity} label="Návštěvy / měs" value={ga4.during_per_month?.sessions} before={ga4.before_per_month?.sessions} accent="violet" tip="Per-měsíční průměr GA4 sessions na webu." />
          <KpiTile icon={Users} label="Noví uživatelé / měs" value={ga4.during_per_month?.new_users} before={ga4.before_per_month?.new_users} accent="violet" tip="Noví uživatelé poprvé na webu." />
          <KpiTile icon={MousePointerClick} label="SEO clicks / měs" value={ga4.during_per_month?.gsc_clicks} before={ga4.before_per_month?.gsc_clicks} accent="emerald" tip="Kliknutí na web ze Search výsledků." />
          <KpiTile icon={Eye} label="SEO impressions / měs" value={ga4.during_per_month?.gsc_impressions} before={ga4.before_per_month?.gsc_impressions} accent="emerald" tip="Kolikrát se web zobrazil ve Search výsledcích." />
        </div>

        {/* Posledních 30 dní vs. předchozích 30 dní — rolling window pro krátkodobý trend */}
        {data.last30 && (
          <>
            <div className="text-sm font-semibold text-slate-700 mb-2 mt-4">Posledních 30 dní (vs. předchozích 30 dní)</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <KpiTile icon={Activity} label="Návštěvy" value={data.last30.ga4_current?.sessions} before={data.last30.ga4_previous?.sessions} accent="violet" tip="GA4 sessions za posledních 30 dní vs. předchozích 30 dní (rolling). Krátkodobý trend." />
              <KpiTile icon={Users} label="Noví uživatelé" value={data.last30.ga4_current?.new_users} before={data.last30.ga4_previous?.new_users} accent="violet" tip="Noví uživatelé na webu, posledních 30 dní vs. předchozích 30 dní." />
              <KpiTile icon={MousePointerClick} label="SEO clicks" value={data.last30.gsc_current?.clicks} before={data.last30.gsc_previous?.clicks} accent="emerald" tip="Kliknutí ze Search Console, posledních 30 dní vs. předchozích 30 dní." />
              <KpiTile icon={Eye} label="SEO impressions" value={data.last30.gsc_current?.impressions} before={data.last30.gsc_previous?.impressions} accent="emerald" tip="Zobrazení ve Search výsledcích, posledních 30 dní vs. předchozích 30 dní." />
            </div>
          </>
        )}

        {/* Kvartál — poslední uzavřený vs. předposlední (přesně Q1=Jan-Mar atd.) */}
        {data.quarter && (
          <>
            <div className="text-sm font-semibold text-slate-700 mb-2 mt-4">
              Kvartál {data.quarter.period?.last?.label} <span className="text-slate-400 font-normal">vs.</span> {data.quarter.period?.prev?.label}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <KpiTile icon={Activity} label="Návštěvy" value={data.quarter.ga4_current?.sessions} before={data.quarter.ga4_previous?.sessions} accent="violet" tip={`Sessions za poslední uzavřený kvartál (${data.quarter.period?.last?.label}) vs. předposlední (${data.quarter.period?.prev?.label}). Dlouhodobý trend.`} />
              <KpiTile icon={Users} label="Noví uživatelé" value={data.quarter.ga4_current?.new_users} before={data.quarter.ga4_previous?.new_users} accent="violet" tip={`Noví uživatelé, kvartální porovnání ${data.quarter.period?.last?.label} vs. ${data.quarter.period?.prev?.label}.`} />
              <KpiTile icon={MousePointerClick} label="SEO clicks" value={data.quarter.gsc_current?.clicks} before={data.quarter.gsc_previous?.clicks} accent="emerald" tip={`Kliknutí z GSC, ${data.quarter.period?.last?.label} vs. ${data.quarter.period?.prev?.label}.`} />
              <KpiTile icon={Eye} label="SEO impressions" value={data.quarter.gsc_current?.impressions} before={data.quarter.gsc_previous?.impressions} accent="emerald" tip={`Zobrazení v GSC, ${data.quarter.period?.last?.label} vs. ${data.quarter.period?.prev?.label}.`} />
            </div>
          </>
        )}

        {/* Web traffic graf */}
        <KartaGrafu ikona={Activity} nazev="Měsíční trend návštěv webu" popis="Sleduj zda po startu spolupráce počet návštěv roste">
<WebTrafficChart trend={ga4.monthly_trend} collabStartISO={period?.collab_start} />
        </KartaGrafu>

        {/* Top stránky + Top klíčová slova (Search Console, posledních 30 dní) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
          <TopList
            icon={Globe}
            title="Top stránky"
            subtitle="Nejvíce návštěv ze Search výsledků za posledních 30 dní"
            rows={(data.gsc?.top_pages_last30 || data.gsc?.top_pages_during || []).slice(0, 10)}
            labelKey="page"
            mapLabel={(s) => {
              try { return s.replace(/^https?:\/\/[^/]+/, "") || "/"; }
              catch { return s; }
            }}
            primaryKey="clicks"
            primaryLabel="kliknutí"
            secondaryKey="impressions"
            secondaryLabel="zobrazení"
          />
          <TopList
            icon={MousePointerClick}
            title="Top klíčová slova"
            subtitle="Co lidé hledají, když najdou web. Posledních 30 dní"
            rows={(data.gsc?.top_queries_last30 || data.gsc?.top_queries_during || []).slice(0, 10)}
            labelKey="query"
            primaryKey="clicks"
            primaryLabel="kliknutí"
            secondaryKey="impressions"
            secondaryLabel="zobrazení"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100 pt-4 text-center text-xs text-slate-400">
        Zdroje: Business Manager CSV, GA4, Search Console, leady. Cache 6 hodin.
      </div>
    </Stranka>
  );
}
